import { useState, useEffect } from 'react';
import { Box, Flex, Text, IconButton, Tooltip, Button, Select, useToast } from '@chakra-ui/react';
import Sidebar from '../components/Sidebar';
import MobileBottomNav from '../components/MobileBottomNav';
import { Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import { FiVolume2, FiVolumeX, FiExternalLink } from 'react-icons/fi';
import NotificationBell from '../components/NotificationBell';
import { authAPI } from '../services/api';
import { setStoredTokenForRole } from '../utils/authStorage';

const EDU_PLATFORM_URL = 'https://edu.naturaledu.net';

const AdminLayout = () => {
    const { user } = useAuth();
    const { audioBlocked, enableAudio } = useWebSocket();
    const toast = useToast();
    const [launchingEdu, setLaunchingEdu] = useState(false);
    const institutionName = user?.institution?.name;

    // [A1] 機構切換：僅 super_admin 顯示下拉選單，其餘角色維持原本的靜態機構名稱顯示
    const isSuperAdmin = user?.role === 'super_admin';
    const [switchableInstitutions, setSwitchableInstitutions] = useState([]);
    const [switchingInstitution, setSwitchingInstitution] = useState(false);

    useEffect(() => {
        if (!isSuperAdmin) return;
        let isMounted = true;
        authAPI.getInstitutions()
            .then((res) => {
                if (isMounted) setSwitchableInstitutions(Array.isArray(res.data) ? res.data : []);
            })
            .catch((error) => {
                console.error('無法取得機構列表', error);
            });
        return () => { isMounted = false; };
    }, [isSuperAdmin]);

    const handleSwitchInstitution = async (e) => {
        const nextInstitutionId = Number(e.target.value);
        if (!nextInstitutionId || nextInstitutionId === user?.institution_id) return;

        setSwitchingInstitution(true);
        try {
            const res = await authAPI.switchInstitution(nextInstitutionId);
            const newToken = res.data?.access_token;
            if (newToken) {
                // 換發新 token 後才 reload：後端 JWT payload 內嵌 institution_id，
                // WebSocket /ws/broadcast 交握會優先信任 token 內的值做 Zero-Query 快速路徑，
                // 若沿用舊 token，切換後 WS 房間不會跟著變（見後端 admin.py switch-institution 註解）。
                // C3 auth 翻新：authStorage.js 的 token 寫入改為 async，這裡本來就在 async 函式
                // 內，補一個 await 即可；switch-institution 端點不回傳 refresh token（非 C2 新
                // 端點），既有 refresh token（若有）維持不動，不在本次改動範圍內。
                await setStoredTokenForRole(newToken);
            }
            window.location.reload();
        } catch (error) {
            console.error('切換機構失敗', error);
            toast({
                title: '切換機構失敗',
                description: error.response?.data?.detail || '請稍後再試',
                status: 'error',
                isClosable: true,
            });
            setSwitchingInstitution(false);
        }
    };

    const openEduPlatform = async () => {
        setLaunchingEdu(true);
        try {
            const res = await authAPI.getSSOToken();
            const token = res.data?.sso_token;
            if (!token) throw new Error('未取得 SSO Token');
            window.open(`${EDU_PLATFORM_URL}/sso?token=${encodeURIComponent(token)}`, '_blank');
        } catch (e) {
            toast({ title: '無法開啟教育平台', description: e.message, status: 'error', duration: 3000 });
        } finally {
            setLaunchingEdu(false);
        }
    };

    return (
        <Flex h="100dvh" overflow="hidden" bg="neutral.50"
                /* 【共用頁的前景色 —— 2026-08-13】
                   LunchManagementPage / SettingsPage / Announcement* / LeavesPage /
                   CommunicationBookPage 這幾頁**桌面與手機共用**，但兩邊底色差很多：
                     桌面 AdminLayout = neutral.50 淺米(L*94.9) → 深灰字達標
                     手機外殼        = canvas 霧灰橄欖(L*61.6) → 同一組深灰字只有 2.0~3.0，不達標
                   同一個色票不可能同時適合兩者，所以由**外殼各自定義**這三個變數，
                   共用頁只寫 var(--fg-*)，落在哪個外殼就自動吃到對的值。 */
            sx={{
                '--fg-weak': 'var(--chakra-colors-neutral-400)',
                '--fg-muted': 'var(--chakra-colors-neutral-500)',
                '--fg-body': 'var(--chakra-colors-neutral-600)',
            }}
        >
            {/* Sidebar 固定在左側 (手機版隱藏) */}
            <Box display={{ base: 'none', md: 'block' }}>
                <Sidebar />
            </Box>

            {/* 主要內容區域 (改為 Flex Column 以包含 Header) */}
            <Box flex="1" display="flex" flexDirection="column" pb={{ base: '70px', md: 0 }}>

                {/* 頂部 Header (顯示機構名稱) */}
                <Flex
                    as="header"
                    align="center"
                    justify="flex-end"
                    px={8}
                    py={4}
                    bg="white"
                    shadow="sm"
                    borderBottom="1px solid"
                    borderColor="neutral.200"
                >
                    <Tooltip label="前往天籟教育平台（自動登入）" placement="bottom">
                        <Button
                            size="sm"
                            variant="outline"
                            colorScheme="teal"
                            leftIcon={<FiExternalLink />}
                            onClick={openEduPlatform}
                            isLoading={launchingEdu}
                            loadingText="跳轉中"
                            mr={3}
                            display={{ base: 'none', md: 'flex' }}
                        >
                            教育平台
                        </Button>
                    </Tooltip>

                    {isSuperAdmin ? (
                        <Tooltip label="切換檢視的機構（僅超級管理員可見）" placement="bottom">
                            <Select
                                size="sm"
                                maxW="220px"
                                mr={4}
                                fontWeight="bold"
                                color="brand.700"
                                bg="white"
                                value={user?.institution_id ?? ''}
                                onChange={handleSwitchInstitution}
                                isDisabled={switchingInstitution || switchableInstitutions.length === 0}
                                aria-label="切換檢視機構"
                            >
                                {!user?.institution_id && <option value="">請選擇機構</option>}
                                {switchableInstitutions.map((inst) => (
                                    <option key={inst.id} value={inst.id}>{inst.name}</option>
                                ))}
                            </Select>
                        </Tooltip>
                    ) : (
                        institutionName && (
                            <Text fontSize="3xl" fontWeight="bold" color="brand.700" mr={4}>
                                {institutionName}
                            </Text>
                        )
                    )}

                    <Box mr={4}>
                        <NotificationBell />
                    </Box>

                    <Tooltip label={audioBlocked ? "點擊啟用音效 (廣播提醒)" : "音效已啟用"}>
                        <IconButton
                            icon={audioBlocked ? <FiVolumeX /> : <FiVolume2 />}
                            aria-label="Toggle Audio"
                            onClick={enableAudio}
                            colorScheme={audioBlocked ? "red" : "green"}
                            variant="ghost"
                            size="lg"
                        />
                    </Tooltip>
                </Flex >

                {/* Content Area: Allow scrolling (both X and Y) so mobile tables can be viewed */}
                <Box flex="1" p={{ base: 2, md: 8 }} overflowY="auto" position="relative" w="100%" minW="0" id="main-content">
                    <Outlet />
                </Box>

                {/* 手機版底部導航 */}
                <MobileBottomNav />
            </Box >
        </Flex >
    );
};

export default AdminLayout;
